import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as BackIcon,
  Description as OffresIcon,
  SpaceDashboardOutlined as DashboardIcon,
  QueryStatsOutlined as FinanceIcon,
  People as ClientsIcon,
  Handyman as ServicesIcon,
  AccountBalanceWalletOutlined as CaisseIcon,
  PrecisionManufacturing as ProductionIcon,
  ReceiptLongOutlined as DocumentsIcon,
  Inventory2Outlined as StockIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationsBell from '../../components/NotificationsBell';
import HeaderChatButton from '../../components/HeaderChatButton';
import { moduleColors } from '../../theme/tableTheme';

const drawerWidth = 280;
const collapsedDrawerWidth = 64;
export const COMPTOIR_COLOR = moduleColors.comptoir.primary;
export const COMPTOIR_HOVER = moduleColors.comptoir.hover;

const MENU: { text: string; icon: React.ReactNode; path: string; miniModuleName?: string }[] = [
  // no mini-module: any Comptoir access (the page hides the blocks the user can't view)
  { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/comptoir/dashboard' },
  { text: 'Offres commerciales', icon: <OffresIcon />, path: '/comptoir/offres', miniModuleName: 'Offres commerciales' },
  { text: 'Services & Projets', icon: <ServicesIcon />, path: '/comptoir/services', miniModuleName: 'Services & Projets' },
  { text: 'Suivi de production', icon: <ProductionIcon />, path: '/comptoir/production', miniModuleName: 'Suivi de production' },
  { text: 'Stock matière', icon: <StockIcon />, path: '/comptoir/stock-matiere', miniModuleName: 'Suivi de production' },
  { text: 'Suivi financier', icon: <FinanceIcon />, path: '/comptoir/finance', miniModuleName: 'Services & Projets' },
  { text: 'Clients', icon: <ClientsIcon />, path: '/comptoir/clients', miniModuleName: 'Clients' },
  { text: 'Caisse', icon: <CaisseIcon />, path: '/comptoir/caisse', miniModuleName: 'Caisse' },
  { text: 'Bons & Documents', icon: <DocumentsIcon />, path: '/comptoir/documents', miniModuleName: 'Bons & Documents' },
];

const ComptoirLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, canAccessModule } = usePermissions();

  const items = MENU.map((item) => ({
    ...item,
    hasAccess: item.miniModuleName ? hasPermission('Comptoir', 'view', item.miniModuleName) : canAccessModule('Comptoir'),
  }));

  const drawer = (
    <div>
      <Toolbar>
        {desktopOpen ? (
          <Button
            fullWidth
            startIcon={<BackIcon />}
            onClick={() => navigate('/dashboard')}
            variant="outlined"
            size="small"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'flex-start' }}
          >
            Retour au Menu Principal
          </Button>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Box>
        )}
      </Toolbar>
      <Divider />
      <List>
        {items.map((item) => {
          const isDisabled = !item.hasAccess;
          const isActive = !isDisabled && location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.text} disablePadding>
              <Tooltip title={isDisabled ? 'Accès refusé' : ''} placement="right" arrow>
                <span style={{ width: '100%' }}>
                  <ListItemButton
                    selected={isActive}
                    onClick={!isDisabled ? () => navigate(item.path) : undefined}
                    disabled={isDisabled}
                    sx={{
                      minHeight: 48,
                      justifyContent: desktopOpen ? 'initial' : 'center',
                      px: desktopOpen ? 2.5 : 0,
                      '&.Mui-selected': {
                        backgroundColor: COMPTOIR_COLOR,
                        color: '#fff',
                        '&:hover': { backgroundColor: COMPTOIR_HOVER },
                      },
                      '&.Mui-disabled': { opacity: 0.6, cursor: 'not-allowed' },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 0,
                      mr: desktopOpen ? 3 : 0,
                      display: 'flex',
                      justifyContent: 'center',
                      color: isActive ? '#fff' : 'inherit',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {desktopOpen && (
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }} />
                    )}
                  </ListItemButton>
                </span>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)` },
          ml: { sm: desktopOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
          bgcolor: COMPTOIR_COLOR,
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <IconButton color="inherit" aria-label="toggle drawer" edge="start" onClick={() => setDesktopOpen(!desktopOpen)} sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Comptoir
          </Typography>
          <HeaderChatButton />
          <NotificationsBell />
          <ThemeToggle />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        onMouseEnter={() => { if (!desktopOpen) setDesktopOpen(true); }}
        sx={{ width: { sm: desktopOpen ? drawerWidth : collapsedDrawerWidth }, flexShrink: { sm: 0 }, transition: 'width 0.3s ease' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open={desktopOpen}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        onClick={() => { if (desktopOpen) setDesktopOpen(false); }}
        sx={{
          flexGrow: 1,
          pt: 3,
          pb: 12, // room under the last rows: the floating chat button would cover their actions
          px: 2,
          width: { sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)` },
          transition: 'width 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default ComptoirLayout;
